import { Link } from "react-router-dom";
import { ArrowRight, Truck, Globe2, Network, BarChart3, ShieldCheck, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO } from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

const IMG = "/images/ll100-montage.jpg";

export default function SmartLabelsLogistics() {
  useSEO({
    title: "Smart Labels for Logistics | Shipment Monitoring & Tracking Devices",
    description: "Disposable smart labels and shipment tracking devices for logistics — lane visibility, exception detection, and proof of journey across multi-leg networks.",
    canonical: "/smart-labels-for-logistics",
  });

  return (
    <div data-testid="page-logistics">
      <PageHero
        eyebrow="Use case"
        title="Smart Labels for Logistics"
        subtitle="Lane visibility, exception detection, and proof of journey — without the reverse-logistics overhead of reusable trackers."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Why logistics teams use them</p>
            <h2 className="h2">Visibility that survives the lane.</h2>
            <p className="lead mt-5">
              Logistics teams need shipment-level data that holds up across
              carriers, transfers, and partner depots — whether you call it
              shipment monitoring, GPS tracking for packages, or simply
              shipment tracking devices, the underlying problem is the same:
              you need clean data that travels with the shipment, without
              committing to hardware that has to come back.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "Independent visibility across multi-carrier and multi-leg lanes.",
                "Clean attribution of dwell, exceptions, and route deviations.",
                "Single-use means single accountability — one label, one shipment.",
                "Data flows into GoAndTrack for ops, partners, and stakeholders.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {linkifyGoAndTrack(b)}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white">
              <div className="aspect-[5/4] overflow-hidden">
                <img src={IMG} alt="Disposable smart labels — peel, apply, scan, deployed across logistics shipments" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Where they fit</p>
            <h2 className="h2">Logistics scenarios.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
            {[
              { icon: Truck, k: "Domestic & cross-border road", v: "Single-leg and multi-stop road movements where you need granular journey data without recovering hardware from drivers or depots." },
              { icon: Globe2, k: "European linehaul", v: "Cross-border lanes where carrier handovers and customs touchpoints create visibility gaps that disposable labels close cleanly." },
              { icon: Network, k: "Partner & 3PL networks", v: "Lanes that traverse partner-owned warehouses where you have no operational authority to recover reusable trackers." },
              { icon: BarChart3, k: "New lane validation", v: "Validating performance, dwell, and exceptions on new routes before contracting at volume." },
              { icon: ShieldCheck, k: "Service-level evidence", v: "Independent journey data for SLA, claims, and dispute resolution with carriers and 3PLs." },
              { icon: Truck, k: "Returns & reverse flows", v: "Visibility on returns lanes where customer-side recovery of reusable hardware is unrealistic." },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <b.icon className="h-5 w-5 text-brand" />
                <h3 className="h3 mt-4">{b.k}</h3>
                <p className="body-text mt-2 text-sm">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedLinks
        title="Related reading for logistics teams"
        links={[
          { to: "/smart-labels-for-high-value-shipments", label: "Smart Labels for High-Value Shipments — evidence-grade visibility" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
          { to: "/gps-tracker-for-packages", label: "GPS Tracker for Packages — package-level view" },
          { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels — BLE technology explained" },
        ]}
      />

      <CTAStrip
        title="Get visibility on the lanes that matter."
        body="Tell us the route. We'll structure a pilot with measurable outputs."
      />
    </div>
  );
}
